import { getApiTable, listApiTableRowSets, pickApiRowValue } from "./qmsApiUtils";
import { getVaultAuditLogEntries } from "./qmsVaultAuditLogUtils";
import {
  formatVaultTableDate,
  getVaultPersonDisplayStatus,
  getVaultSavedApprovers,
  getVaultSavedReviewers,
  isVaultPersonSent,
} from "./qmsVaultWorkspaceUtils";

function readRowText(row, ...keys) {
  const value = pickApiRowValue(row, ...keys);
  if (value == null) return "";
  return String(value).trim();
}

function parseDateValue(raw) {
  if (raw == null) return 0;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const numeric = new Date(raw).getTime();
    return Number.isNaN(numeric) ? 0 : numeric;
  }

  if (raw instanceof Date) {
    const time = raw.getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  const text = String(raw).trim();
  if (!text || text === "—") return 0;

  // .NET JSON date: /Date(1712345678901)/ or /Date(1712345678901+0530)/
  const msMatch = text.match(/\/Date\((-?\d+)(?:[+-]\d+)?\)\//);
  if (msMatch) {
    const time = Number(msMatch[1]);
    return Number.isFinite(time) ? time : 0;
  }

  let parsed = Date.parse(text);
  if (!Number.isNaN(parsed)) return parsed;

  // dd/MM/yyyy[ hh:mm[:ss][ tt]]
  const dmy = text.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([AaPp][Mm]))?)?$/,
  );
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]) - 1;
    const year = Number(dmy[3]);
    let hours = dmy[4] != null ? Number(dmy[4]) : 0;
    const minutes = dmy[5] != null ? Number(dmy[5]) : 0;
    const seconds = dmy[6] != null ? Number(dmy[6]) : 0;
    const ampm = (dmy[7] || "").toLowerCase();
    if (ampm === "pm" && hours < 12) hours += 12;
    if (ampm === "am" && hours === 12) hours = 0;
    const time = new Date(year, month, day, hours, minutes, seconds).getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  const swapped = text.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3");
  if (swapped !== text) {
    parsed = Date.parse(swapped);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return 0;
}

export function parseVaultTimelineDate(row, ...keys) {
  for (const key of keys) {
    const raw = pickApiRowValue(row, key);
    const parsed = parseDateValue(raw);
    if (parsed) return parsed;
  }
  return 0;
}

function preferDisplayDate(...values) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text && text !== "—") return text;
  }
  return "—";
}

const REVIEW_CYCLE_ROLE = {
  mode: "reviewers",
  historyTable: "ReviewReviewerHistory",
  stageTable: "ReviewStageHistory",
  savedRowsFn: getVaultSavedReviewers,
  idField: "ReviewerId",
  idKeys: ["ReviewerId", "reviewerId"],
  nameKeys: ["ReviewerName", "reviewerName"],
  sentDateKeys: ["ReviewSentDate", "reviewSentDate"],
  actionDateKeys: ["ReviewedDate", "reviewedDate"],
  commentKeys: ["ReviewerComment", "reviewerComment"],
  statusField: "ReviewStatus",
  sentDateField: "ReviewSentDate",
  completeFlagKeys: ["IsReviewComplete", "isReviewComplete"],
  completedAtKeys: ["ReveiwCompletedDateTime", "reveiwCompletedDateTime"],
  cycleNumberKeys: ["CycleNumber", "cycleNumber"],
  stageStart: "in review",
  stageEnds: ["review completed"],
  auditStartNeedle: "document sent for review",
  auditEndNeedles: ["review completed"],
  personRow: {
    id: "ReviewerId",
    name: "ReviewerName",
    sentDate: "ReviewSentDate",
    assignedDateTime: "AssignedDateTime",
    actionDate: "ReviewedDate",
    status: "ReviewStatus",
    comment: "ReviewerComment",
    completedAt: "ReveiwCompletedDateTime",
    isComplete: "IsReviewComplete",
  },
};

const APPROVAL_CYCLE_ROLE = {
  mode: "approvers",
  historyTable: "ApprovalApproverHistory",
  stageTable: "ApprovalStageHistory",
  savedRowsFn: getVaultSavedApprovers,
  idField: "ApproverId",
  idKeys: ["ApproverId", "approverId"],
  nameKeys: ["ApproverName", "approverName"],
  sentDateKeys: ["ApproveSentDate", "approveSentDate"],
  actionDateKeys: ["ApprovedDate", "approvedDate"],
  commentKeys: ["ApproverComment", "approverComment"],
  statusField: "ApproveStatus",
  sentDateField: "ApproveSentDate",
  completeFlagKeys: ["IsApprovalComplete", "isApprovalComplete"],
  completedAtKeys: ["ApprovalCompletedDateTime", "approvalCompletedDateTime"],
  cycleNumberKeys: ["CycleNumber", "cycleNumber"],
  stageStart: "in approval",
  // Owner complete OR any approver reject both close the cycle.
  stageEnds: ["approval completed", "approval rejected"],
  auditStartNeedle: "document sent for approval",
  auditEndNeedles: ["approval completed", "document rejected", "approval rejected"],
  personRow: {
    id: "ApproverId",
    name: "ApproverName",
    sentDate: "ApproveSentDate",
    assignedDateTime: "AssignedDateTime",
    actionDate: "ApprovedDate",
    status: "ApproveStatus",
    comment: "ApproverComment",
    completedAt: "ApprovalCompletedDateTime",
    isComplete: "IsApprovalComplete",
  },
};

function getCycleRole(mode) {
  return mode === "approvers" ? APPROVAL_CYCLE_ROLE : REVIEW_CYCLE_ROLE;
}

function hasMemberHistoryMarker(row, role) {
  return Boolean(
    pickApiRowValue(row, "AssignedDateTimeRaw", "assignedDateTimeRaw")
      || pickApiRowValue(row, "AssignedDateTime", "assignedDateTime")
      || pickApiRowValue(row, ...role.completeFlagKeys) != null
      || pickApiRowValue(row, ...role.cycleNumberKeys) != null,
  );
}

function isMemberHistoryRow(row, role) {
  if (!row || typeof row !== "object") return false;
  return Boolean(
    pickApiRowValue(row, ...role.idKeys)
      && pickApiRowValue(row, ...role.nameKeys),
  );
}

export function getVaultCycleMemberHistory(response, mode = "reviewers") {
  const role = getCycleRole(mode);
  const named = getApiTable(response, role.historyTable);
  if (named.length && named.some((row) => hasMemberHistoryMarker(row, role))) {
    return named;
  }

  let best = [];
  listApiTableRowSets(response).forEach((rows) => {
    const memberRows = (rows || []).filter((row) => isMemberHistoryRow(row, role));
    if (!memberRows.length) return;
    if (!memberRows.some((row) => hasMemberHistoryMarker(row, role))) return;
    if (memberRows.length > best.length) best = memberRows;
  });

  return best.length ? best : named;
}

export function getVaultCycleStageHistory(response, mode = "reviewers") {
  const role = getCycleRole(mode);
  return getApiTable(response, role.stageTable);
}

export function getVaultReviewReviewerHistory(response) {
  return getVaultCycleMemberHistory(response, "reviewers");
}

export function getVaultReviewStageHistory(response) {
  return getVaultCycleStageHistory(response, "reviewers");
}

export function getVaultApprovalApproverHistory(response) {
  return getVaultCycleMemberHistory(response, "approvers");
}

export function getVaultApprovalStageHistory(response) {
  return getVaultCycleStageHistory(response, "approvers");
}

function normalizeMemberHistoryRow(row, index, role) {
  const memberId = readRowText(row, ...role.idKeys);
  const sentDate = formatVaultTableDate(pickApiRowValue(row, ...role.sentDateKeys));
  const actionDate = formatVaultTableDate(pickApiRowValue(row, ...role.actionDateKeys));
  const status = getVaultPersonDisplayStatus(
    row,
    role.statusField,
    role.sentDateField,
  );
  const isSent = isVaultPersonSent(row, role.statusField, role.sentDateField);
  const assignedSort =
    parseVaultTimelineDate(row, "AssignedDateTimeRaw", "assignedDateTimeRaw")
    || parseVaultTimelineDate(row, "AssignedDateTime", "assignedDateTime")
    || parseVaultTimelineDate(row, "SystemDateTime", "systemDateTime")
    || parseVaultTimelineDate(row, ...role.sentDateKeys);
  const sentSort =
    parseVaultTimelineDate(row, ...role.sentDateKeys) || assignedSort;

  return {
    id: `${memberId}-${assignedSort || sentSort || index}`,
    memberId,
    memberName: readRowText(row, ...role.nameKeys) || "—",
    // Backward-compatible aliases used by older review timeline code paths.
    reviewerId: memberId,
    reviewerName: readRowText(row, ...role.nameKeys) || "—",
    sentDate,
    sentBy: readRowText(row, "SentBy", "sentBy") || "—",
    actionDate,
    reviewedDate: actionDate,
    comment: readRowText(row, ...role.commentKeys) || "—",
    status: isSent ? status : "Assigned",
    isActive: (() => {
      const rawActive = row?.IsActive ?? row?.isActive;
      // Saved rows may omit IsActive; treat missing as active. History rows include it.
      return rawActive == null ? true : Number(rawActive) === 1;
    })(),
    isComplete:
      Number(pickApiRowValue(row, ...role.completeFlagKeys) ?? 0) === 1,
    isReviewComplete:
      Number(pickApiRowValue(row, ...role.completeFlagKeys) ?? 0) === 1,
    completedAt: formatVaultTableDate(
      pickApiRowValue(row, ...role.completedAtKeys),
    ),
    reviewCompletedAt: formatVaultTableDate(
      pickApiRowValue(row, ...role.completedAtKeys),
    ),
    assignedAt: formatVaultTableDate(
      pickApiRowValue(row, "AssignedDateTime", "assignedDateTime"),
    ),
    assignedSort,
    sentSort,
  };
}

function findLogMatch(entries, actionNeedle, targetSort, windowMs = 120000) {
  if (!targetSort) return null;

  let best = null;
  let bestDelta = windowMs + 1;

  (entries || []).forEach((entry) => {
    const action = String(entry.actionName || "").trim().toLowerCase();
    if (!action.includes(actionNeedle)) return;

    const delta = Math.abs(entry.sortValue - targetSort);
    if (delta <= windowMs && delta < bestDelta) {
      best = entry;
      bestDelta = delta;
    }
  });

  return best;
}

function normalizeMemberId(value) {
  return String(value || "")
    .trim()
    .replace(/^\{|\}$/g, "")
    .toLowerCase();
}

/** Current cycle: one card per person — latest active assignment wins. */
function keepLatestActiveMemberPerPerson(rows) {
  const byId = new Map();
  (rows || []).forEach((row) => {
    if (!row?.isActive) return;
    const key = normalizeMemberId(row.memberId);
    if (!key) return;
    const existing = byId.get(key);
    const nextSort = row.assignedSort || row.sentSort || 0;
    const prevSort = existing ? existing.assignedSort || existing.sentSort || 0 : -1;
    if (!existing || nextSort >= prevSort) {
      byId.set(key, row);
    }
  });
  return [...byId.values()];
}

function getActiveCycle(cycles) {
  return (cycles || []).find((cycle) => cycle.status !== "completed") || null;
}

/** Assign by owner-complete boundaries, newest cycle first. */
function findCycleForMember(chronological, anchor) {
  if (!anchor || !chronological.length) return null;

  for (let index = chronological.length - 1; index >= 0; index -= 1) {
    const cycle = chronological[index];
    const prevEnd = index > 0 ? chronological[index - 1].endSort || 0 : 0;
    const endBound = cycle.endSort || Number.POSITIVE_INFINITY;

    // After previous owner-complete (exclusive) and up to this owner-complete (inclusive).
    if (prevEnd && anchor <= prevEnd) continue;
    if (anchor > endBound) continue;
    return cycle;
  }

  return null;
}

function ensureOpenCycleForLateMembers(cycles, memberRows) {
  if (!cycles.length) return cycles;

  const chronological = [...cycles].sort((a, b) => a.startSort - b.startSort);
  if (getActiveCycle(chronological)) return chronological;

  const lastCycle = chronological[chronological.length - 1];
  const lastEnd = lastCycle?.endSort || 0;
  if (!lastEnd) return chronological;

  const lateRows = (memberRows || []).filter((row) => {
    const anchor = row.assignedSort || row.sentSort;
    return anchor > lastEnd;
  });
  if (!lateRows.length) return chronological;

  const firstLate = [...lateRows].sort(
    (a, b) => (a.assignedSort || a.sentSort) - (b.assignedSort || b.sentSort),
  )[0];
  const startSort = firstLate.assignedSort || firstLate.sentSort;

  chronological.push({
    status: "active",
    startedAt: firstLate.assignedAt || firstLate.sentDate || "",
    startedBy: firstLate.sentBy || "—",
    startComments: "",
    completedAt: "",
    completedBy: "",
    completeComments: "",
    startSort,
    endSort: 0,
    members: [],
    reviewers: [],
  });

  return chronological;
}

function getMemberCompletedSortValue(row) {
  return (
    parseDateValue(row?.completedAt)
    || parseDateValue(row?.reviewCompletedAt)
    || parseDateValue(row?.actionDate)
    || parseDateValue(row?.reviewedDate)
    || 0
  );
}

function isMemberMarkedComplete(row) {
  const status = String(row?.status || "").trim().toLowerCase();
  // Owner cycle-close stamps IsReviewComplete on every member — that is not
  // this person's own submit.
  if (status.includes("complete") || status.includes("approved")) return true;
  if (status.includes("reject")) return true;
  return false;
}

/**
 * Pending members first; completed members after (ASC by completed date).
 */
function sortMembersPendingFirst(members) {
  return [...(members || [])].sort((a, b) => {
    const aDone = isMemberMarkedComplete(a);
    const bDone = isMemberMarkedComplete(b);
    if (aDone !== bDone) return aDone ? 1 : -1;

    if (aDone && bDone) {
      const diff = getMemberCompletedSortValue(a) - getMemberCompletedSortValue(b);
      if (diff !== 0) return diff;
    }

    return (a.assignedSort || a.sentSort || 0) - (b.assignedSort || b.sentSort || 0);
  });
}

function syncCycleMemberAliases(cycle) {
  const members = cycle.members || cycle.reviewers || [];
  cycle.members = members;
  cycle.reviewers = members;
  return cycle;
}

function assignMembersToAllCycles(cycles, memberRows) {
  if (!cycles.length) return cycles;

  const chronological = ensureOpenCycleForLateMembers(cycles, memberRows);
  chronological.forEach((cycle) => {
    cycle.members = [];
    cycle.reviewers = [];
  });

  // Replace caller's array contents with possibly extended cycle list.
  cycles.length = 0;
  chronological.forEach((cycle) => cycles.push(cycle));

  if (!memberRows.length) return cycles;

  const sortedMembers = [...memberRows].sort(
    (a, b) => (a.assignedSort || a.sentSort) - (b.assignedSort || b.sentSort),
  );
  const usedRowIds = new Set();
  const activeCycle = getActiveCycle(chronological);

  sortedMembers.forEach((row) => {
    if (usedRowIds.has(row.id)) return;

    const anchor = row.assignedSort || row.sentSort;
    let target = findCycleForMember(chronological, anchor);

    // Only undated / post-complete active rows fall into the open cycle.
    if (!target && activeCycle && (!anchor || anchor > (activeCycle.startSort || 0) - 1)) {
      if (!anchor || !activeCycle.endSort) {
        target = activeCycle;
      }
    }

    // Last resort: nearest cycle by start time (keeps past rows out of current when possible).
    if (!target && chronological.length) {
      let best = chronological[0];
      let bestDelta = Number.POSITIVE_INFINITY;
      chronological.forEach((cycle) => {
        const delta = Math.abs((anchor || 0) - (cycle.startSort || 0));
        if (delta < bestDelta) {
          bestDelta = delta;
          best = cycle;
        }
      });
      target = best;
    }

    if (!target) return;
    usedRowIds.add(row.id);
    target.members.push(row);
    target.reviewers = target.members;
  });

  chronological.forEach((cycle) => {
    cycle.members = sortMembersPendingFirst(cycle.members);
    cycle.reviewers = cycle.members;
  });

  return cycles;
}

function finalizeCycleMembers(cycles) {
  (cycles || []).forEach((cycle) => {
    const raw = (cycle.members || cycle.reviewers || []).filter((row) => row.isActive);
    const members = sortMembersPendingFirst(
      cycle.status === "completed" ? raw : keepLatestActiveMemberPerPerson(raw),
    );
    cycle.members = members;
    cycle.reviewers = members;
  });
}

function mergeActiveSavedMembers(cycles, response, role) {
  const activeCycle = getActiveCycle(cycles);
  if (!activeCycle) return;

  const chronological = [...cycles].sort((a, b) => a.startSort - b.startSort);
  const cycleStartBound = Math.max(0, (activeCycle.startSort || 0) - 180000);

  const savedRows = role
    .savedRowsFn(response)
    .map((row, index) => normalizeMemberHistoryRow(row, index, role))
    .filter((row) => row.memberId && row.isActive);

  if (!savedRows.length) return;

  savedRows.forEach((row) => {
    const anchor = row.assignedSort || row.sentSort;
    // Do not pull older-cycle active rows into the current cycle.
    if (anchor && anchor < cycleStartBound) return;
    if (anchor && findCycleForMember(chronological, anchor) !== activeCycle) {
      return;
    }

    const savedId = normalizeMemberId(row.memberId);
    const members = activeCycle.members || [];
    const index = members.findIndex(
      (item) => item.isActive && normalizeMemberId(item.memberId) === savedId,
    );

    if (index >= 0) {
      const existing = members[index];
      members[index] = {
        ...existing,
        ...row,
        id: existing.id || row.id,
        isActive: true,
        sentDate: preferDisplayDate(row.sentDate, existing.sentDate),
        assignedAt: preferDisplayDate(row.assignedAt, existing.assignedAt),
        // Prefer current saved-row completion values (do not keep stale past-cycle dates).
        actionDate:
          preferDisplayDate(row.actionDate) !== "—"
            ? row.actionDate
            : "—",
        reviewedDate:
          preferDisplayDate(row.actionDate) !== "—"
            ? row.actionDate
            : "—",
        completedAt:
          preferDisplayDate(row.completedAt) !== "—"
            ? row.completedAt
            : "—",
        reviewCompletedAt:
          preferDisplayDate(row.completedAt) !== "—"
            ? row.completedAt
            : "—",
        status:
          row.status && row.status !== "Assigned"
            ? row.status
            : existing.status || row.status,
      };
      return;
    }

    activeCycle.members.push(row);
  });

  activeCycle.members = sortMembersPendingFirst(
    keepLatestActiveMemberPerPerson(activeCycle.members),
  );
  activeCycle.reviewers = activeCycle.members;
}

function buildCyclesFromStageHistory(stageRows, logEntries, role) {
  const sortedStages = [...(stageRows || [])]
    .map((row, index) => ({
      stage: readRowText(row, "DocumentStage", "documentStage"),
      dateTime: formatVaultTableDate(
        pickApiRowValue(row, "StageDateTime", "stageDateTime"),
      ),
      actionBy: readRowText(row, "ActionBy", "actionBy") || "—",
      sortValue:
        parseVaultTimelineDate(row, "StageDateTimeRaw", "stageDateTimeRaw")
        || parseVaultTimelineDate(row, "StageDateTime", "stageDateTime"),
      index,
    }))
    .sort((a, b) => a.sortValue - b.sortValue || a.index - b.index);

  const cycles = [];
  let current = null;

  sortedStages.forEach((stage) => {
    const normalizedStage = stage.stage.toLowerCase();

    // New cycle only starts after previous was owner-completed.
    // Extra start-stage / re-sends while open stay in the same cycle.
    if (normalizedStage === role.stageStart) {
      if (current && !current.endSort) {
        return;
      }

      const startLog = findLogMatch(logEntries, role.auditStartNeedle, stage.sortValue);
      current = {
        status: "active",
        startedAt: stage.dateTime,
        startedBy: stage.actionBy,
        startComments: startLog?.comments && startLog.comments !== "N/A" ? startLog.comments : "",
        completedAt: "",
        completedBy: "",
        completeComments: "",
        startSort: stage.sortValue,
        endSort: 0,
        members: [],
        reviewers: [],
      };
      return;
    }

    const stageEnds = role.stageEnds || (role.stageEnd ? [role.stageEnd] : []);
    if (stageEnds.includes(normalizedStage) && current) {
      const endNeedles = role.auditEndNeedles || (role.auditEndNeedle ? [role.auditEndNeedle] : []);
      let completeLog = null;
      for (const needle of endNeedles) {
        completeLog = findLogMatch(logEntries, needle, stage.sortValue);
        if (completeLog) break;
      }
      current.completedAt = stage.dateTime;
      current.completedBy = stage.actionBy;
      current.completeComments =
        completeLog?.comments && completeLog.comments !== "N/A" ? completeLog.comments : "";
      current.endSort = stage.sortValue;
      current.status = "completed";
      current.closedByReject = normalizedStage.includes("reject");
      cycles.push(syncCycleMemberAliases(current));
      current = null;
    }
  });

  if (current) {
    cycles.push(syncCycleMemberAliases(current));
  }

  return cycles;
}

function buildCyclesFromAuditLog(logEntries, role) {
  const endNeedles = role.auditEndNeedles || (role.auditEndNeedle ? [role.auditEndNeedle] : []);
  const workflowEntries = (logEntries || [])
    .filter((entry) => {
      const action = String(entry.actionName || "").trim().toLowerCase();
      return (
        action.includes(role.auditStartNeedle)
        || endNeedles.some((needle) => action.includes(needle))
      );
    })
    .sort((a, b) => a.sortValue - b.sortValue);

  const cycles = [];
  let current = null;

  workflowEntries.forEach((entry) => {
    const action = String(entry.actionName || "").trim().toLowerCase();

    if (action.includes(role.auditStartNeedle)) {
      if (current && !current.endSort) {
        return;
      }

      current = {
        status: "active",
        startedAt: entry.actionDateTime,
        startedBy: entry.actionBy,
        startComments: entry.comments && entry.comments !== "N/A" ? entry.comments : "",
        completedAt: "",
        completedBy: "",
        completeComments: "",
        startSort: entry.sortValue,
        endSort: 0,
        members: [],
        reviewers: [],
      };
      return;
    }

    if (endNeedles.some((needle) => action.includes(needle)) && current) {
      current.completedAt = entry.actionDateTime;
      current.completedBy = entry.actionBy;
      current.completeComments =
        entry.comments && entry.comments !== "N/A" ? entry.comments : "";
      current.endSort = entry.sortValue;
      current.status = "completed";
      current.closedByReject = action.includes("reject");
      cycles.push(syncCycleMemberAliases(current));
      current = null;
    }
  });

  if (current) {
    cycles.push(syncCycleMemberAliases(current));
  }

  return cycles;
}

export function buildVaultPeopleCycleTimeline(response, mode = "reviewers") {
  const role = getCycleRole(mode);
  const memberRows = getVaultCycleMemberHistory(response, mode).map((row, index) =>
    normalizeMemberHistoryRow(row, index, role),
  );
  const stageRows = getVaultCycleStageHistory(response, mode);
  const logEntries = getVaultAuditLogEntries(response);

  const cycles = stageRows.length
    ? buildCyclesFromStageHistory(stageRows, logEntries, role)
    : buildCyclesFromAuditLog(logEntries, role);

  assignMembersToAllCycles(cycles, memberRows);
  mergeActiveSavedMembers(cycles, response, role);
  finalizeCycleMembers(cycles);

  return cycles.sort((a, b) => (b.startSort || 0) - (a.startSort || 0));
}

export function buildVaultReviewCycleTimeline(response) {
  return buildVaultPeopleCycleTimeline(response, "reviewers");
}

export function buildVaultApprovalCycleTimeline(response) {
  return buildVaultPeopleCycleTimeline(response, "approvers");
}

export function mapCycleMemberToPersonRow(member, mode = "reviewers") {
  const role = getCycleRole(mode);
  const fields = role.personRow;
  const sentDate = preferDisplayDate(member.sentDate, member.assignedAt);
  const actionDate = preferDisplayDate(member.actionDate, member.reviewedDate);
  const completedAt = preferDisplayDate(member.completedAt, member.reviewCompletedAt);

  return {
    [fields.id]: member.memberId || member.reviewerId,
    [fields.name]: member.memberName || member.reviewerName,
    [fields.sentDate]: sentDate !== "—" ? sentDate : "",
    [fields.assignedDateTime]:
      member.assignedAt && member.assignedAt !== "—"
        ? member.assignedAt
        : "",
    [fields.actionDate]: actionDate !== "—" ? actionDate : "",
    [fields.status]: member.status === "Assigned" ? "" : member.status,
    [fields.comment]: member.comment !== "—" ? member.comment : "",
    [fields.completedAt]: completedAt !== "—" ? completedAt : "",
    IsActive: member.isActive ? 1 : 0,
    [fields.isComplete]: member.isComplete || member.isReviewComplete ? 1 : 0,
  };
}

export function mapCycleReviewerToPersonRow(reviewer) {
  return mapCycleMemberToPersonRow(reviewer, "reviewers");
}

export function mapCycleApproverToPersonRow(approver) {
  return mapCycleMemberToPersonRow(approver, "approvers");
}

export function hasVaultPeopleCycleTimeline(response, mode = "reviewers") {
  return buildVaultPeopleCycleTimeline(response, mode).length > 0;
}

export function hasVaultReviewCycleTimeline(response) {
  return hasVaultPeopleCycleTimeline(response, "reviewers");
}

export function hasVaultApprovalCycleTimeline(response) {
  return hasVaultPeopleCycleTimeline(response, "approvers");
}
